// Pure logic: value lookup, difference/domain computation, search matching, hash state
// parse/serialise. No DOM, no MapLibre — this module must be testable with plain `node`.
// See docs/DESIGN.md §3 (data model) and §6 (frontend design).

import type { DimSpec, PairwiseEncoding, PlzIndex, PlzRecord, Registry, RegistryMetric, ScaleSpec } from "./registry";

export type Month = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export type MonthSelection = Month | "year";
export type ViewMode = "diff" | "abs";

export interface AppState {
  metric: string;
  month: MonthSelection;
  ref: string;
  view: ViewMode;
  /** "Reachable within" filter, in the pairwise metric's unit (minutes). 0 means "Any" (off). */
  reach: number;
}

// ---------------------------------------------------------------------------
// Value lookup
// ---------------------------------------------------------------------------

/** True when a metric has no `month` dimension: a single scalar per PLZ. */
export function isScalarMetric(metric: Pick<RegistryMetric, "dims">): boolean {
  return metric.dims.month === undefined;
}

/**
 * Value for a PLZ under the current month selection.
 * - Scalar metric (no month dim): `values` is a 1-element array `[v]`; month selection is ignored.
 * - Monthly metric, specific month: values[month - 1].
 * - Monthly metric, "year": the registry's annual rule applied to all 12 months.
 */
export function valueFor(
  values: (number | null)[] | undefined,
  month: MonthSelection,
  dimSpec: DimSpec | undefined,
): number | null {
  if (!values || values.length === 0) return null;
  if (dimSpec === undefined) {
    // scalar metric: single value, no month dimension
    return values[0] ?? null;
  }
  if (month === "year") return annualValue(values, dimSpec.annual);
  const v = values[month - 1];
  return v === undefined ? null : v;
}

/**
 * Annual rule per docs/DESIGN.md §6.3 / PLAN acceptance: "mean" is the arithmetic mean of
 * all 12 months and is null if ANY month is null (not a mean of only the non-null months —
 * a partial year is not a reliable annual figure). "sum" behaves the same way. "none" means
 * the Year button is hidden entirely, so callers should not reach this with annual === "none".
 */
export function annualValue(values: (number | null)[], annual: DimSpec["annual"]): number | null {
  if (annual === "none") return null;
  if (values.some((v) => v === null || v === undefined)) return null;
  const nums = values as number[];
  if (annual === "sum") return nums.reduce((a, b) => a + b, 0);
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

/** value[plz] - value[ref]; null propagates (missing data has no defined difference). */
export function difference(value: number | null, refValue: number | null): number | null {
  if (value === null || refValue === null) return null;
  return value - refValue;
}

// ---------------------------------------------------------------------------
// Domain computation (docs/DESIGN.md §6.1)
// ---------------------------------------------------------------------------

/** The p-th percentile (0..1) of a sorted-ascending numeric array using linear interpolation. */
function percentileSorted(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0];
  const idx = p * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  const frac = idx - lo;
  return sorted[lo] * (1 - frac) + sorted[hi] * frac;
}

/**
 * Diverging domain: the registry's fixed domain if given, else symmetric
 * ±(95th percentile of |diff|) across all PLZ. Zero is always the midpoint.
 * Falls back to [-1, 1] when there is no data at all (avoids a degenerate 0-width domain).
 */
export function diffDomain(scale: ScaleSpec, diffs: (number | null)[]): [number, number] {
  if (scale.domain) return scale.domain;
  const abs = diffs.filter((v): v is number => v !== null && Number.isFinite(v)).map((v) => Math.abs(v));
  if (abs.length === 0) return [-1, 1];
  abs.sort((a, b) => a - b);
  const p95 = percentileSorted(abs, 0.95);
  const bound = p95 > 0 ? p95 : 1;
  return [-bound, bound];
}

/** Sequential (absolute-value) domain: registry domain if given, else [min, max] of the values. */
export function absDomain(scale: ScaleSpec, values: (number | null)[]): [number, number] {
  if (scale.domain) return scale.domain;
  const nums = values.filter((v): v is number => v !== null && Number.isFinite(v));
  if (nums.length === 0) return [0, 1];
  let lo = nums[0];
  let hi = nums[0];
  for (const v of nums) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (lo === hi) {
    // degenerate domain (all PLZ equal): widen slightly so a scale can still be built.
    lo -= 1;
    hi += 1;
  }
  return [lo, hi];
}

// ---------------------------------------------------------------------------
// Pairwise (reference x PLZ) matrices — docs/design-isochrones.md §2.6/2.7, §3.3
// ---------------------------------------------------------------------------

/** A decoded pairwise cell: minutes, `null` (no value: no stop/zone or no connection), or the
 * string `"beyond"` (byte equalled `encoding.beyond`: reachable only past the recorded cap). */
export type ReachValue = number | null | "beyond";

/**
 * Decode one row (or any byte buffer) of a pairwise uint8 matrix per `encoding`:
 * byte === missing -> null; byte === beyond -> "beyond"; else byte * scale (minutes).
 * `bytes` may be a Uint8Array (from a Range fetch) or a plain array (tests).
 */
export function decodeRow(bytes: ArrayLike<number>, encoding: PairwiseEncoding): ReachValue[] {
  const out: ReachValue[] = new Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b === encoding.missing) out[i] = null;
    else if (b === encoding.beyond) out[i] = "beyond";
    else out[i] = b * encoding.scale;
  }
  return out;
}

/**
 * Which entries of a decoded row are "reachable" under the "Reachable within" filter.
 * `bandMinutes <= 0` means "Any": the filter is off, so everything (including beyond/missing
 * cells and the reference's own 0-minute diagonal) counts as reachable. Otherwise a cell is
 * reachable only if it decoded to a number at or below the band.
 */
export function reachSet(row: ReachValue[], bandMinutes: number): boolean[] {
  if (bandMinutes <= 0) return row.map(() => true);
  return row.map((v) => typeof v === "number" && v <= bandMinutes);
}

/** "Any" for 0/negative, "2 h" on the hour, else "2 h 15 min". */
export function formatBandLabel(minutes: number): string {
  if (minutes <= 0) return "Any";
  const whole = Math.round(minutes);
  const h = Math.floor(whole / 60);
  const m = whole % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Snap an arbitrary minute value onto the reach slider's grid; 0 means "Any" (no filter).
 * Values above the grid maximum also mean "Any", since the position past the end is unlimited. */
export function snapToStep(minutes: number, grid: { min: number; max: number; step: number }): number {
  if (minutes <= 0 || minutes > grid.max) return 0;
  const clamped = Math.max(grid.min, Math.min(grid.max, minutes));
  return grid.min + Math.round((clamped - grid.min) / grid.step) * grid.step;
}

// ---------------------------------------------------------------------------
// Search (accent-insensitive)
// ---------------------------------------------------------------------------

/** Fold accents/diacritics and case for comparison: "Zürich" -> "zurich". */
export function foldAccents(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export interface SearchHit {
  plz: string;
  record: PlzRecord;
}

/**
 * Reference-PLZ search: matches a PLZ prefix, or a case/accent-insensitive substring of any
 * of `names` or `gemeinde`. Returns up to `limit` hits, PLZ-prefix matches first.
 */
export function searchPlz(index: PlzIndex, query: string, limit = 8): SearchHit[] {
  const q = query.trim();
  if (q === "") return [];
  const qFolded = foldAccents(q);
  const byPlz: SearchHit[] = [];
  const byName: SearchHit[] = [];
  for (const plz of Object.keys(index)) {
    const record = index[plz];
    if (plz.startsWith(q)) {
      byPlz.push({ plz, record });
      continue;
    }
    const candidates = [...record.names, record.gemeinde];
    if (candidates.some((c) => foldAccents(c).includes(qFolded))) {
      byName.push({ plz, record });
    }
  }
  byPlz.sort((a, b) => a.plz.localeCompare(b.plz));
  byName.sort((a, b) => a.record.name.localeCompare(b.record.name));
  return [...byPlz, ...byName].slice(0, limit);
}

/** Format a search hit for display, e.g. "8001 Zürich (ZH)". */
export function formatHit(hit: SearchHit): string {
  return `${hit.plz} ${hit.record.name} (${hit.record.canton})`;
}

// ---------------------------------------------------------------------------
// URL hash state: #m=tmax&mo=7&ref=8001&view=diff
// ---------------------------------------------------------------------------

function currentCalendarMonth(now: Date = new Date()): Month {
  return (now.getMonth() + 1) as Month;
}

/** Default state per docs/DESIGN.md §6.1: first metric, current month, "8001" or first PLZ, diff,
 * reach filter off ("Any"). */
export function defaultState(registry: Registry, plzIndex: PlzIndex, now: Date = new Date()): AppState {
  const metric = registry.metrics[0]?.id ?? "";
  const plzKeys = Object.keys(plzIndex);
  const ref = "8001" in plzIndex ? "8001" : (plzKeys[0] ?? "");
  return { metric, month: currentCalendarMonth(now), ref, view: "diff", reach: 0 };
}

function parseMonth(raw: string | null): MonthSelection | null {
  if (raw === null) return null;
  if (raw === "year") return "year";
  const n = Number(raw);
  if (Number.isInteger(n) && n >= 1 && n <= 12) return n as Month;
  return null;
}

/** `reach=120` (minutes); absent, non-numeric, or negative all fall back to the default (0). */
function parseReach(raw: string | null): number | null {
  if (raw === null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

/**
 * Parse `location.hash` (with or without leading "#") into an AppState, falling back to
 * `defaultState()` field-by-field for anything missing or invalid. Unknown metric/ref/view
 * values also fall back, so a stale or hand-edited hash never crashes the app.
 */
export function parseHash(hash: string, registry: Registry, plzIndex: PlzIndex, now: Date = new Date()): AppState {
  const fallback = defaultState(registry, plzIndex, now);
  const clean = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(clean);

  const metricRaw = params.get("m");
  const metric = metricRaw && registry.metrics.some((m) => m.id === metricRaw) ? metricRaw : fallback.metric;

  const month = parseMonth(params.get("mo")) ?? fallback.month;

  const refRaw = params.get("ref");
  const ref = refRaw && refRaw in plzIndex ? refRaw : fallback.ref;

  const viewRaw = params.get("view");
  const view: ViewMode = viewRaw === "abs" ? "abs" : viewRaw === "diff" ? "diff" : fallback.view;

  const reach = parseReach(params.get("reach")) ?? fallback.reach;

  return { metric, month, ref, view, reach };
}

/** Serialise state back to a `#...` hash string (round-trips with parseHash). `reach` is
 * omitted entirely when it is 0 ("Any"), so a default-state hash stays exactly as before. */
export function serializeHash(state: AppState): string {
  const params = new URLSearchParams();
  params.set("m", state.metric);
  params.set("mo", String(state.month));
  params.set("ref", state.ref);
  params.set("view", state.view);
  if (state.reach > 0) params.set("reach", String(state.reach));
  return "#" + params.toString();
}
